import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {
    useDeletePricesMutation,
    usePricesInfiniteQuery,
} from './usePricesQuery';
import {usePricesSearch} from './usePricesSearch';
import {usePricesTableColumns} from './usePricesTableColumns';
import {PricesFilters} from './usePricesFilter';
import {Price} from '../types';

/**
 * Default sort: by NICKNAME, ascending.
 *
 * ⚠️ This is NOT the "newest first on a creation timestamp" default the
 * checklist prefers, and the reason is concrete: `listPrices` does not select
 * `created` at all (see services/queries.ts), so there is no creation timestamp
 * in the payload to sort on. §3a's own rule is that a nullable key "silently
 * degrades to insertion order when every row lacks it" — here EVERY row lacks
 * it, so defaulting to `created` would be sorting on nothing while claiming to
 * sort on time.
 *
 * `nickname` is what an operator scans a price list by. It is immutable in
 * practice (renaming a price is rare) and it is stable, which is the property
 * that actually matters — the list must not reshuffle under someone reading it.
 * Nulls sort last, so unnamed prices collect at the bottom rather than at the
 * top.
 *
 * Restoring the timestamp default means adding `created` to the GraphQL
 * selection set first. That is a query change, not a sort-config change.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<Price> = {
    columnAccessor: 'nickname',
    direction: 'asc',
};

/**
 * FNP-918: `unitAmount` arrives as a STRING OF CENTS and is normalised here to
 * a NUMBER of currency units.
 *
 * The old hook did `parseInt(record.unitAmount) / 100` and assigned it back
 * onto a field still TYPED as string, so the value was a number wearing a
 * string's type. That is why sorting it would have compared "100" against "9"
 * lexically had anyone tried.
 *
 * `parseInt` on a null/undefined/non-numeric value yields NaN, which sorts
 * unpredictably and renders as "NaN" in a cell. Guarded to `undefined` instead,
 * which the column renders as an em-dash and the shared comparator sorts last.
 */
const normalizeRecord = (record: Price): Price => {
    const cents = Number(record.unitAmount);

    return {
        ...record,
        unitAmount: Number.isFinite(cents)
            ? (cents / 100) as unknown as string
            : undefined as unknown as string,
    };
};

/**
 * Which fields a keyword matches against. `id` is included because a Stripe
 * price id (`price_...`) is what an operator pastes in from the Stripe
 * dashboard.
 */
const matchesKeyword = (item: Price, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.nickname,
        item.currency,
        item.product,
        item.billingScheme,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active filter. Filters are exact matches on
 * a stringified field; an empty value means the field is unset and is ignored,
 * so a cleared drawer field does not exclude everything.
 */
const matchesFilters = (item: Price, filters: PricesFilters): boolean =>
    Object.entries(filters)
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof Price];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ SCOPE OF SORT AND FILTER: the pages FETCHED SO FAR.
 *
 * `listPrices` accepts a `PricesFilterInput`, and this module uses it for
 * exactly one narrowing — `product`, which the product detail page passes — so
 * THAT filter is genuinely applied by the server. The drawer's active/currency
 * choices are not fields that input declares, and there is no sort argument at
 * all, so both are applied in the browser over the loaded window. Sorting page
 * 1 does not sort the dataset, and a keyword matches only rows already
 * received.
 *
 * This is a property of the token-pagination model against this API, not a bug
 * and not an oversight. It is also exactly why the footer states a LOADED count
 * and never a total: see DataTableTokenPagination, which is passed `filterTerm`
 * so a filtered view says "N of M loaded records match" rather than implying it
 * searched everything.
 */
export const usePrices = (
    productId?: string,
    filters: PricesFilters = {},
) => {
    const query = usePricesInfiniteQuery(productId);
    const {columns} = usePricesTableColumns();
    const {keyword} = usePricesSearch();
    const deleteMutation = useDeletePricesMutation();

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: Price[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean)
        .map(normalizeRecord);

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare. Do not
    // hand-roll one; the null rule is what a hand-rolled version gets wrong.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<Price>(matched, DEFAULT_SORT_STATUS);

    // Sorted separately rather than taking `sorted` from the hook: `matched`
    // is rebuilt every render (new object identities from the map above), and
    // sortRecords is the same comparator applied to the current array.
    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        deleteRecords: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                deleteMutation.mutate(ids);
            }
        },
        deleteMutation,
    };
};
